import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as XLSX from 'xlsx';

import { IStoreState } from 'store/modules';
import { ActionCtor } from 'store/modules/main';

const backgroundColor = {
  victim: 'linear-gradient(180deg, #e63528 0%, #801d16 100%)',
  rescue: 'linear-gradient(180deg, #0086ff 0%, #005099 100%)',
};

interface StyledButtonProps {
  readonly variant: 'victim' | 'rescue';
}

interface CenterMessageProps {
  userinfo?: any;
  onClick?: (phone: string, type: string) => void;
}

const CenterMessage = ({ userinfo, onClick }: CenterMessageProps) => {
  const dispatch = useDispatch();
  const checkUser = useSelector((store: IStoreState) => store.MainReducer.checkUser);
  const passengers = useSelector((store: IStoreState) => store.MainReducer.passengers);
  const [vNumber, setVNumber] = useState('');
  const [rNumber, setRNumber] = useState('');
  const [type, setType] = useState('V');
  const inputFileRef = useRef<HTMLInputElement>(null);
  const allRescues = useSelector((store: IStoreState) => store.MainReducer.rescues);
  const prevCheckUserRef = useRef<string[]>([]);

  const isNumberInRescueList = (phone: string): boolean => {
    if (!allRescues || !Array.isArray(allRescues)) return false;
    const target = phone.replace(/[^0-9]/g, '');

    const search = (nodes: any[]): boolean => {
      for (const node of nodes) {
        if (node.lv === 4 && node.children) {
          if (node.children.some((m: any) => (m.value || '').toString().replace(/[^0-9]/g, '') === target)) return true;
        }
        if (node.children && search(node.children)) return true;
      }
      return false;
    };
    return search(allRescues);
  };

  const handleVChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVNumber(event.target.value);
  };

  const handleRChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRNumber(value);

    const phones = value
      .split(',')
      .map((s) => s.replace(/[^0-9]/g, '').trim())
      .filter((s) => s.length === 11);

    dispatch(ActionCtor.updateRNumberPhones(phones));
  };

  useEffect(() => {
    if (checkUser === undefined) return;

    const isSame =
      prevCheckUserRef.current.length === checkUser.length &&
      prevCheckUserRef.current.every((val, index) => val === checkUser[index]);

    if (isSame) return;

    prevCheckUserRef.current = checkUser;

    setRNumber((prev) => {
      const currentEntries = prev
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const selectedPhones = checkUser.map(String);

      const preserved = currentEntries.filter((entry) => {
        const pureDigits = entry.replace(/[^0-9]/g, '');
        if (pureDigits.length === 11 && isNumberInRescueList(pureDigits)) {
          return selectedPhones.includes(pureDigits);
        }
        return true;
      });

      const newItems = selectedPhones.filter(
        (phone) => !currentEntries.some((e) => e.replace(/[^0-9]/g, '') === phone)
      );

      return [...preserved, ...newItems].join(', ');
    });
  }, [checkUser]);

  const onClickSend = (type: string) => {
    if (onClick !== undefined) {
      onClick(type === 'V' ? vNumber : rNumber, type);
      type === 'V' ? setVNumber('') : setRNumber('');
    }
  };

  const onOnclickSet = (type: string) => {
    setType(type);
    inputFileRef.current!.click();
    // if (type === 'V') {
    //   //setVNumber('01037326271, 01085708949');
    //   setVNumber('01047540194, 01045495244, 01050241952, 01027774950');
    // } else {
    //   setRNumber('01047540194, 01045495244, 01050241952, 01027774950');
    // }
  };

  const reacExcelFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files !== null) {
      const file = e.target.files[0];
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const index = 0;
      const worksheet = workbook.Sheets[workbook.SheetNames[index]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        range: 1,
      });

      const phone = jsonData
        .map(function (val: any) {
          return val[1] + '(' + val[0] + ')';
        })
        .join(', ');
      setVNumber(phone);
    }
    e.target.value = '';
  };

  const readExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const reader = new FileReader();
    if (input.files !== null) {
      const organization: any[] = [];
      reader.onload = function () {
        const data = reader.result;
        const workBook = XLSX.read(data, { type: 'binary' });
        workBook.SheetNames.forEach(function (sheetName) {
          // const rows = XLSX.utils.sheet_to_json(workBook.Sheets[sheetName], { header: 1 });
          // console.log('Excel: ', rows);
          // rows.forEach((item: any) => {
          //   console.log('items: ', item);
          // });

          const rows = XLSX.utils.sheet_to_json(workBook.Sheets[sheetName]);
          let first_last_value = null;
          let second_last_value = null;
          let third_last_value = null;
          rows.forEach((r: any) => {
            first_last_value = r['경찰서'] = r['경찰서'] || first_last_value;
            second_last_value = r['파출소'] = r['파출소'] || second_last_value;
            if (r['이름'] !== undefined || r['전화번호'] !== undefined) {
              third_last_value = r['구조대'] = r['구조대'] || third_last_value;
            }
          });
          organization.push({ 경찰청: sheetName, rows });
        });
        dispatch(ActionCtor.addRescue(organization));
      };
      reader.readAsBinaryString(input.files[0]);
    }
  };

  useEffect(() => {
    if (passengers !== undefined) {
      const result = passengers.map((item) => `${item.phone}(${item.name})`);
      setVNumber(result.toString());
    } else {
      setVNumber('');
    }
  }, [passengers]);

  return (
    <CenterMessageTemplateWrap>
      <HorizontalWrap>
        <Title variant={'rescue'}>구조대{'\n'}동원</Title>
        <InputNumber value={rNumber} onChange={handleRChange}></InputNumber>
        <SendButton variant={'rescue'} onClick={() => onClickSend('R')}>
          전송
        </SendButton>
        <ContactButton style={{ display: 'none' }} variant={'rescue'} onClick={() => onOnclickSet('R')}>
          구조대{'\n'}리스트
        </ContactButton>
      </HorizontalWrap>
      <HorizontalWrap>
        <Title variant={'victim'}>요구조자{'\n'}위치요청</Title>
        <InputNumber value={vNumber} onChange={handleVChange}></InputNumber>
        <SendButton variant={'victim'} onClick={() => onClickSend('V')}>
          전송
        </SendButton>
        {userinfo?.belong === '마량파출소' && (
          <ContactButton variant={'victim'} onClick={() => onOnclickSet('V')}>
            승선명부{'\n'}가져오기
          </ContactButton>
        )}
      </HorizontalWrap>
      <input type="file" ref={inputFileRef} style={{ display: 'none' }} onChange={reacExcelFile} />
    </CenterMessageTemplateWrap>
  );
};

const CenterMessageTemplateWrap = styled.div`
  width: 100%;
  height: 11.125rem;
  background: #010910;
  border: 1px solid #000000;
  white-space: pre-line;
`;
const HorizontalWrap = styled.div`
  height: 4rem;
  display: flex;
  margin: 1rem 1rem;
`;
const Title = styled.div<StyledButtonProps>`
  width: 8.125rem;
  color: #fff;
  text-align: center;
  font-family: Inter;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ffffff;
  background: ${(props) => backgroundColor[props.variant]};
`;
const InputNumber = styled.input`
  flex: 1;
  color: #fff;
  text-align: center;
  font-family: Inter;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  background-color: #4b4b4b;
  border: none;
  outline: none;
  margin: 0 2px;
`;
const SendButton = styled.button<StyledButtonProps>`
  width: 6.25rem;
  color: #fff;
  text-align: center;
  font-family: Inter;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  background: ${(props) => backgroundColor[props.variant]};
  border: none;
  cursor: pointer;
`;
const ContactButton = styled.button<StyledButtonProps>`
  width: 6.375rem;
  margin-left: 20px;
  color: #fff;
  text-align: center;
  font-family: Inter;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  background: ${(props) => backgroundColor[props.variant]};
  border: none;
  cursor: pointer;
`;

export default CenterMessage;
